<script lang="ts" setup>
	import type { IconLibProps } from '../tools/icon.vue'

	export type GuideListContext = Array<{
		tab: string
		label: string
		description: string
		list: {
			iconTag: IconLibProps
			title: string
			description: string
			imageSrc: string
			show?: boolean
		}[]
	}>

	const {
		guideList = [
			{
				tab: 'hospital',
				label: 'Hospital Visit',
				description: 'The best hospital experience for your staff',
				list: [
					{
						iconTag: {
							dir: 'random',
							icon: 'gps'
						},
						title: 'Locate the hospital of your choice',
						description:
							'Easily find the hospital that suits your needs by browsing our extensive network of healthcare facilities.',
						imageSrc: '/v1727082818/hospital-visit-gif_js3l8n.gif',
						show: true
					},
					{
						title: 'Present HMO ID',
						description:
							"Once you've chosen your hospital, simply present your HMO ID at the reception desk.",
						iconTag: {
							dir: 'random',
							icon: 'id-card'
						},
						imageSrc: '/v1727082838/present-hmo_h2trr2.gif'
					},
					{
						title: 'Get access to treatment instantly',
						description: "With your HMO ID ready, you'll immediately access treatment.",
						iconTag: {
							dir: 'random',
							icon: 'clipboard-text'
						},
						imageSrc: '/v1727082827/instant-treatment-access_q0l34b.gif'
					}
				]
			},
			{
				tab: 'virtual',
				label: 'Virtual Consultation',
				description: 'Your staff can access healthcare support professionals from their comfort',
				list: [
					{
						iconTag: {
							dir: 'random',
							icon: 'monitor-mobile'
						},
						title: 'Online consultation',
						description: 'Contact a healthcare professional from your comfort.',
						imageSrc: '/v1727082835/online-consultation_vbujcd.gif',
						show: true
					},
					{
						iconTag: {
							dir: 'fill',
							icon: 'special-consultation',
							pathClass: 'fill-white',
							height: 16,
							width: 16
						},
						title: 'Get answers fast',
						description: 'Receive fast health assessment and advice from a medical professional.',
						imageSrc: '/v1727082978/chat_bmnr01.webp'
					},
					{
						iconTag: {
							dir: 'random',
							icon: 'drug'
						},
						title: 'Pick up your prescription',
						description: 'Pick up your prescriptions from the nearest pharmacy within our network',
						imageSrc: '/v1727362076/instant-treatment-access_qz7ddk.webp'
					}
				]
			}
		]
	} = defineProps<{
		guideList?: GuideListContext
	}>()

	const selectedTab = shallowRef('')
	const list = ref<GuideListContext>([])
	const imageCollection = ref<HTMLElement | null>(null)

	const activeGuide = computed(() => {
		return list.value.find(item => item.tab === selectedTab.value) ?? null
	})

	const selectTab = (payload: string) => {
		if (payload) {
			selectedTab.value = payload
		}
	}
	const focusOnItem = (prop: GuideListContext[number]['list'][number], index: number) => {
		if (activeGuide.value?.list) {
			activeGuide.value.list.forEach(item => (item.show = false))
			prop.show = true

			// Switch image
			if (imageCollection.value) {
				const imageWrapper = imageCollection.value
				const imgEl = imageWrapper.querySelector(`[data-image-id="${index}"]`) as HTMLElement
				if (imgEl) {
					imageWrapper.scrollTop = imgEl.offsetHeight * index
				}
			}
		}
	}

	onMounted(() => {
		if (Array.isArray(guideList)) {
			list.value = guideList
			selectedTab.value = guideList[0].tab
		}
	})
	watch(selectedTab, (newVal, initial) => {
		if (newVal !== initial) {
			const activeIndex = activeGuide.value?.list.findIndex(item => item.show) ?? 0
			if (activeGuide.value?.list) {
				const item = activeGuide.value.list[activeIndex]
				if (item) {
					focusOnItem(item, activeIndex)
				}
			}
		}
	})
</script>

<template>
	<div class="bg-[#FFF8E5] w-full py-8">
		<ui-pad>
			<div class="flexed justify-between items-center flex-wrap gap-5">
				<div class="flex-1 max-w-[1188px]">
					<ui-title-header
						:title="activeGuide?.label ?? '---'"
						:ui="{
							size: 'sm:text-[3.25em] text-[2em]',
							lineHeight: 'sm:leading-[72.8px]',
							color: 'text-[#1A1200]'
						}"
						class="custom-transition-2"
					/>
					<p
						class="text-gray-600 text-sm sm:text-xl mt-1.5 sm:leading-[32px]"
						:class="{ 'max-w-[609px]': list.length > 1 }"
					>
						{{ activeGuide?.description ?? '' }}
					</p>
				</div>

				<div v-if="list.length > 1" class="max-w-[480px] w-full flex items-center gap-x-6">
					<button
						v-for="(guide, index) in list"
						:key="index"
						class="sm:min-h-[80px] min-h-[50px] flex-1 font-semibold custom-transition-3 text-center rounded-[40px] text-sm border border-[#1A1200] md:p-0 p-[18px_4px]"
						:class="{
							'bg-[#1A1200] text-white sm:text-2xl': guide.tab === selectedTab,
							'text-[#1A1200] sm:text-xl': guide.tab != selectedTab
						}"
						@click="selectTab(guide.tab)"
					>
						{{ guide.label }}
					</button>
				</div>
			</div>

			<div class="flexed justify-between flex-wrap items-start mt-14 sm:mt-16">
				<div class="lg:max-h-[800px] mb-10 xs:mb-20 lg:mb-0">
					<ul class="relative content-list box-border max-w-[600px]">
						<li
							v-for="(item, index) in activeGuide?.list ?? []"
							:key="`benefit-guide-${index}`"
							class="c-item last:pb-0 py-4 sm:py-10 custom-transition-2 relative cursor-pointer"
							:class="[item.show ? 'active' : 'inactive']"
							@click="focusOnItem(item, index)"
						>
							<div class="flex items-center relative sm:pl-5 pl-1 xs:pl-3">
								<div
									class="flex-none highlight rounded-[20px] z-20 relative -left-1 custom-transition-2"
								/>

								<div class="w-full items-start flex gap-x-3 xs:gap-x-5">
									<div
										class="flex-none ic h-[32px] w-[32px] custom-transition-2 flexed justify-center rounded-full text-white font-bold"
									>
										<tools-icon
											v-if="item.iconTag?.icon && item.iconTag?.icon"
											v-bind="item.iconTag"
										/>
									</div>

									<div>
										<strong class="font-semibold label">
											{{ item.title ?? '---' }}
										</strong>
										<p
											class="tracking-normal description font-normal pt-2 max-w-[458px] sm:leading-[28px] leading-[22.4px]"
										>
											{{ item?.description ?? '---' }}
										</p>
									</div>
								</div>
							</div>
						</li>
					</ul>
				</div>

				<div
					class="flex-none gap-y-10 max-h-[466px] min-h-[580px] bg-[#FFB908] max-w-[466px] rounded-3xl w-full flexed flex-col items-center relative overflow-hidden"
				>
					<div
						class="max-h-[504px] my-auto w-full overflow-hidden invisible-scrollbar snap-y py-5 justify-center flex-col center sticky top-0"
						ref="imageCollection"
					>
						<div
							v-for="(item, index) in activeGuide?.list ?? []"
							:key="'image-' + index"
							class="min-h-[400px] max-w-[245.28px] custom-transition-2 snap-center flexed justify-center my-14 mx-auto w-full rounded-md"
							:class="{
								'highlighted-img opacity-100': item.show,
								'opacity-0': !item.show
							}"
							:data-image-id="index"
						>
							<nuxt-img
								:src="item.imageSrc"
								:alt="item.title"
								loading="lazy"
								class="w-full h-full object-cover min-h-[440px]"
							/>
						</div>
					</div>
				</div>
			</div>
		</ui-pad>
	</div>
</template>

<style scoped>
	.content-list {
		@apply h-full w-full border-[#FFE299]  border-l-[4px] relative;
	}
	.highlight {
		@apply absolute  z-20  border-l-[4px];
	}

	.c-item.inactive .highlight {
		@apply border-transparent h-0;
	}

	.c-item.inactive .ic {
		@apply invisible;
	}
	.c-item.inactive .label {
		@apply text-gray-700 sm:text-xl text-sm font-semibold;
	}
	.c-item.inactive .description {
		@apply text-gray-600 text-sm font-normal;
	}

	/*=======> Active Class ========>*/
	.c-item.active .highlight {
		@apply border-[#FFB908] h-[180px];
	}
	.c-item.active .ic {
		@apply bg-[#FFB908] visible;
	}
	.c-item.active .label {
		@apply text-[#1A1200] sm:text-2xl text-xl;
	}
	.c-item.active .description {
		@apply text-[#1A1200] text-base md:text-xl md:leading-[28px];
	}
</style>
